/**
 * Bay & Stew — Save for Later
 *
 * Cross-device save-for-later, modelled on favorites.js:
 *   - localStorage acts as a fast local cache.
 *   - On load, a server-rendered bootstrap (<script id="bs-saveforlater-bootstrap">)
 *     provides the authoritative list from customer.metafields.saveforlater.handles.
 *   - Writes are debounced and PUT through to the App Proxy endpoint defined by
 *     <meta name="bs-saveforlater-endpoint"> (default: /apps/growth/saved-for-later).
 *   - A background GET runs shortly after boot to catch updates made on other devices.
 *
 * Wire-format on the metafield (list.single_line_text_field):
 *   each entry is "<variantId>|<quantity>|<productHandle>".
 * This legacy wire format cannot carry line-item properties or selling plans.
 * Metadata-bearing items therefore remain local-only (to avoid lossy corruption),
 * while simple items continue cross-device sync via the legacy format.
 *
 * Saved items are separate from Favorites (different key, different metafield).
 *
 * Storage format (localStorage): array of objects:
 *   {
 *     variantId:     number,
 *     productHandle: string,
 *     properties:    object,
 *     sellingPlanId: number|null,
 *     title:         string,
 *     variantTitle:  string,
 *     image:         string|null,
 *     url:           string,
 *     quantity:      number,
 *     price:         number,    // in cents
 *     compareAt:     number|null,
 *   }
 */
(function () {
  'use strict';

  const STORAGE_PREFIX   = 'bs:saved-later:';
  const GUEST_KEY        = 'bs:saved-later:guest';
  const EVENT_CHANGED    = 'bs:sfl:changed';
  const SYNC_DEBOUNCE_MS = 600;

  // -----------------------------------------------------------------------
  // Storage helpers
  // -----------------------------------------------------------------------

  function getCustomerId() {
    const meta = document.querySelector('meta[name="bs-customer-id"]');
    const id   = meta && meta.getAttribute('content');
    return id && id.trim() !== '' ? id.trim() : null;
  }

  function getSyncEndpoint() {
    const meta = document.querySelector('meta[name="bs-saveforlater-endpoint"]');
    const url  = meta && meta.getAttribute('content');
    return url && url.trim() !== '' ? url.trim() : null;
  }

  function getStorageKey() {
    const id = getCustomerId();
    return id ? STORAGE_PREFIX + id : GUEST_KEY;
  }

  function readAll() {
    try {
      const raw    = localStorage.getItem(getStorageKey());
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeLocal(list) {
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(list));
    } catch (e) {
      /* storage full / disabled — ignore */
    }
  }

  /**
   * Persist the list locally, fire a change event so any open UI re-renders,
   * and (unless opted out) schedule a debounced push to the server.
   */
  function writeAll(list, options) {
    options = options || {};
    writeLocal(list);
    document.dispatchEvent(new CustomEvent(EVENT_CHANGED, { detail: { items: list } }));
    if (!options.skipSync) scheduleSync(list);
  }

  function normalizeProperties(properties) {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {};
    const normalized = {};
    Object.keys(properties)
      .sort()
      .forEach((key) => {
        const value = properties[key];
        if (value == null) return;
        const text = String(value);
        if (text === '') return;
        normalized[String(key)] = text;
      });
    return normalized;
  }

  function parseSellingPlanId(value) {
    const parsed = Number(value);
    if (!Number.isSafeInteger(parsed) || parsed <= 0) return null;
    return parsed;
  }

  function normalizeHandle(handle) {
    return String(handle || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
  }

  function hasMetadataIdentity(item) {
    const props = normalizeProperties(item && item.properties);
    if (Object.keys(props).length) return true;
    return parseSellingPlanId(item && (item.sellingPlanId || item.selling_plan)) !== null;
  }

  function isSyncableCrossDeviceItem(item) {
    return !hasMetadataIdentity(item);
  }

  function normalizeSavedItem(item) {
    const variantId = parseInt(item && item.variantId, 10);
    if (!Number.isSafeInteger(variantId) || variantId <= 0) return null;

    return {
      ...item,
      variantId,
      quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
      properties: normalizeProperties(item.properties),
      sellingPlanId: parseSellingPlanId(item.sellingPlanId || item.selling_plan),
    };
  }

  function mergeSavedItemsByIdentity(items) {
    const mergedByKey = new Map();
    const order = [];

    (items || []).forEach((item) => {
      const normalized = normalizeSavedItem(item);
      if (!normalized) return;

      const key = itemIdentityKey(normalized);
      const existing = mergedByKey.get(key);
      if (existing) {
        existing.quantity += normalized.quantity;
        return;
      }

      mergedByKey.set(key, { ...normalized });
      order.push(key);
    });

    return order.map((key) => mergedByKey.get(key));
  }

  function itemIdentityKey(item) {
    const variantId = parseInt(item && item.variantId, 10) || 0;
    const sellingPlanId = parseSellingPlanId(item && (item.sellingPlanId || item.selling_plan));
    const props = normalizeProperties(item && item.properties);
    const propsToken = encodeURIComponent(JSON.stringify(props));
    return `${variantId}::${sellingPlanId || ''}::${propsToken}`;
  }

  function escapeAttrSelectorValue(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  // -----------------------------------------------------------------------
  // Wire-format encode / decode for the metafield
  // -----------------------------------------------------------------------

  /**
   * Encode a single local item to the metafield string format:
   * "<variantId>|<quantity>|<productHandle>"
   */
  function encodeEntry(item) {
    if (!item || !item.variantId || !item.productHandle) return null;
    if (!isSyncableCrossDeviceItem(item)) return null;
    const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
    const handle = normalizeHandle(item.productHandle);
    if (!handle) return null;
    return String(item.variantId) + '|' + qty + '|' + handle;
  }

  function decodeEntry(str) {
    if (typeof str !== 'string') return null;
    const m = str.match(/^(\d+)\|(\d+)\|([a-z0-9-]+)$/);
    if (!m) return null;
    return {
      variantId:     parseInt(m[1], 10),
      quantity:      parseInt(m[2], 10),
      productHandle: m[3],
      properties:    {},
      sellingPlanId: null,
    };
  }

  function encodeList(list) {
    const out = [];
    (list || []).forEach((item) => {
      const enc = encodeEntry(item);
      if (enc) out.push(enc);
    });
    return out;
  }

  function readBootstrap() {
    const node = document.getElementById('bs-saveforlater-bootstrap');
    if (!node) return null;
    const text = (node.textContent || '').trim();
    if (!text) return null;
    try {
      let parsed = JSON.parse(text);
      // Shopify list metafields may come back as a JSON-encoded string.
      if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed); } catch (e) { /* leave as-is */ }
      }
      return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string') : null;
    } catch (e) {
      return null;
    }
  }

  // -----------------------------------------------------------------------
  // Server sync (write-through)
  // -----------------------------------------------------------------------

  let syncTimer      = null;
  let lastSyncedJson = null;

  function scheduleSync(list) {
    if (!getCustomerId() || !getSyncEndpoint()) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => pushToServer(list), SYNC_DEBOUNCE_MS);
  }

  function pushToServer(list) {
    const endpoint = getSyncEndpoint();
    if (!endpoint || !getCustomerId()) return Promise.resolve();
    const handles = encodeList(list);
    const payload = JSON.stringify({ handles: handles });
    if (payload === lastSyncedJson) return Promise.resolve();
    return fetch(endpoint, {
      method:      'PUT',
      credentials: 'same-origin',
      headers:     { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:        payload,
    })
      .then((r) => {
        if (r.ok) lastSyncedJson = payload;
      })
      .catch(() => {
        /* offline / endpoint down — local cache still holds the change */
      });
  }

  function pullFromServer() {
    const endpoint = getSyncEndpoint();
    if (!endpoint || !getCustomerId()) return Promise.resolve(null);
    return fetch(endpoint, {
      method:      'GET',
      credentials: 'same-origin',
      headers:     { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (!body) return null;
        const list = Array.isArray(body) ? body : body.handles;
        return Array.isArray(list) ? list.filter((s) => typeof s === 'string') : null;
      })
      .catch(() => null);
  }

  // -----------------------------------------------------------------------
  // Rehydration: convert decoded "variantId|qty|handle" entries into the
  // full local-cache object shape. Uses the existing cache when possible,
  // otherwise lazily fetches /products/<handle>.js to fill in details.
  // -----------------------------------------------------------------------

  function rehydrateFromEncoded(encodedList) {
    const decoded = (encodedList || []).map(decodeEntry).filter(Boolean);
    if (!decoded.length) return Promise.resolve([]);

    // Existing cache grouped by legacy sync key (variant + handle). Only
    // simple cached items are eligible as rehydrate sources.
    const cache = readAll();
    const cachedBucketsBySyncKey = Object.create(null);
    cache.forEach((it) => {
      const normalized = normalizeSavedItem(it);
      if (!normalized) return;
      if (!isSyncableCrossDeviceItem(normalized)) return;
      const handle = normalizeHandle(normalized.productHandle);
      if (!handle) return;
      const key = `${normalized.variantId}|${handle}`;
      if (!cachedBucketsBySyncKey[key]) cachedBucketsBySyncKey[key] = [];
      cachedBucketsBySyncKey[key].push(normalized);
    });

    function syncKeyForEntry(entry) {
      return `${entry.variantId}|${normalizeHandle(entry.productHandle)}`;
    }

    function hasCachedCoverage(entries) {
      const neededByKey = Object.create(null);
      entries.forEach((entry) => {
        const key = syncKeyForEntry(entry);
        neededByKey[key] = (neededByKey[key] || 0) + 1;
      });
      return Object.keys(neededByKey).every((key) => {
        const available = (cachedBucketsBySyncKey[key] && cachedBucketsBySyncKey[key].length) || 0;
        return available >= neededByKey[key];
      });
    }

    // Group decoded entries by handle so we hit each product endpoint once.
    const byHandle = Object.create(null);
    decoded.forEach((d) => {
      if (!byHandle[d.productHandle]) byHandle[d.productHandle] = [];
      byHandle[d.productHandle].push(d);
    });

    const handles = Object.keys(byHandle);

    return Promise.all(handles.map((handle) => {
      // Skip the network call when every decoded entry for this handle is
      // already in the local cache.
      const allCached = hasCachedCoverage(byHandle[handle]);
      if (allCached) return Promise.resolve({ handle: handle, product: null });

      return fetch('/products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((product) => ({ handle: handle, product: product }))
        .catch(() => ({ handle: handle, product: null }));
    })).then((results) => {
      const productByHandle = Object.create(null);
      results.forEach((res) => { productByHandle[res.handle] = res.product; });

      const remainingCachedBuckets = Object.create(null);
      Object.keys(cachedBucketsBySyncKey).forEach((key) => {
        remainingCachedBuckets[key] = cachedBucketsBySyncKey[key].slice();
      });

      // Preserve the order returned by the server.
      const out = [];
      decoded.forEach((d) => {
        const cachedBucket = remainingCachedBuckets[syncKeyForEntry(d)];
        const cached  = cachedBucket && cachedBucket.length ? cachedBucket.shift() : null;
        const product = productByHandle[d.productHandle];

        if (cached) {
          // Keep cached metadata; update quantity to what the server says.
          out.push(Object.assign({}, cached, {
            variantId: d.variantId,
            productHandle: d.productHandle,
            quantity: d.quantity,
            properties: {},
            sellingPlanId: null,
          }));
          return;
        }

        if (product) {
          const variant = (product.variants || []).find((v) => v.id === d.variantId)
                       || (product.variants || [])[0]
                       || null;
          out.push({
            variantId:     d.variantId,
            productHandle: d.productHandle,
            properties:    {},
            sellingPlanId: null,
            title:         product.title || '',
            variantTitle:  variant ? variant.title : '',
            image:         (variant && variant.featured_image && variant.featured_image.src)
                            || product.featured_image
                            || (product.images && product.images[0])
                            || null,
            url:           '/products/' + d.productHandle
                            + (variant ? '?variant=' + variant.id : ''),
            quantity:      d.quantity,
            price:         variant ? variant.price : (product.price || 0),
            compareAt:     variant && variant.compare_at_price && variant.compare_at_price > variant.price
                            ? variant.compare_at_price : null,
          });
          return;
        }

        // Product fetch failed (e.g. variant deleted) — keep a minimal stub so
        // the entry isn't silently dropped from the server next sync.
        out.push({
          variantId:     d.variantId,
          productHandle: d.productHandle,
          properties:    {},
          sellingPlanId: null,
          title:         '',
          variantTitle:  '',
          image:         null,
          url:           '/products/' + d.productHandle,
          quantity:      d.quantity,
          price:         0,
          compareAt:     null,
        });
      });

      return mergeSavedItemsByIdentity(out);
    });
  }

  function mergeServerHydratedWithLocalMetadata(serverItems, localItems) {
    const localMetadataItems = (localItems || [])
      .map((item) => normalizeSavedItem(item))
      .filter((item) => item && hasMetadataIdentity(item));

    return mergeSavedItemsByIdentity([...(serverItems || []), ...localMetadataItems]);
  }

  /**
   * Hydrate localStorage from the server (bootstrap + background pull).
   *
   * Server is authoritative for logged-in customers. Algorithm mirrors
   * favorites.js so the two behave identically:
   *   1. If bootstrap has items → replace local cache (no sync).
   *   2. If bootstrap is empty but local has items (guest-import case)
   *      → seed the server one-time.
   *   3. Always re-pull shortly after to catch other-device updates.
   */
  function hydrate() {
    if (!getCustomerId()) return;

    const local     = readAll();
    const bootstrap = readBootstrap();

    if (bootstrap && bootstrap.length) {
      rehydrateFromEncoded(bootstrap).then((serverList) => {
        const mergedList = mergeServerHydratedWithLocalMetadata(serverList, local);
        if (JSON.stringify(mergedList) !== JSON.stringify(local)) {
          writeAll(mergedList, { skipSync: true });
        }
        lastSyncedJson = JSON.stringify({ handles: encodeList(serverList) });
      });
    } else if (bootstrap && bootstrap.length === 0 && local.length === 0) {
      lastSyncedJson = JSON.stringify({ handles: [] });
    } else if (local.length && getSyncEndpoint()) {
      // Server is empty but local has items — seed it.
      scheduleSync(local);
    }

    // Background pull a moment later to catch other-device updates.
    if (getSyncEndpoint()) {
      setTimeout(() => {
        pullFromServer().then((serverHandles) => {
          if (!serverHandles) return;
          rehydrateFromEncoded(serverHandles).then((serverList) => {
            const current = readAll();
            const mergedList = mergeServerHydratedWithLocalMetadata(serverList, current);
            if (JSON.stringify(mergedList) !== JSON.stringify(current)) {
              writeAll(mergedList, { skipSync: true });
            }
            lastSyncedJson = JSON.stringify({ handles: encodeList(serverList) });
          });
        });
      }, 1200);
    }
  }

  // -----------------------------------------------------------------------
  // CRUD — save, remove
  // -----------------------------------------------------------------------

  /**
   * Persist a saved item. When the same canonical identity already exists,
   * increment its stored quantity instead of creating a duplicate row.
   */
  function save(item) {
    const normalizedItem = normalizeSavedItem(item);
    if (!normalizedItem) return;

    const list = readAll();
    const key = itemIdentityKey(normalizedItem);
    const idx  = list.findIndex((s) => itemIdentityKey(s) === key);
    if (idx !== -1) {
      list[idx].quantity = Math.max(1, parseInt(list[idx].quantity, 10) || 1) + normalizedItem.quantity;
      list[idx].properties = normalizeProperties(list[idx].properties);
      list[idx].sellingPlanId = parseSellingPlanId(list[idx].sellingPlanId || list[idx].selling_plan);
    } else {
      list.push(normalizedItem);
    }
    writeAll(list);
  }

  function remove(identityOrVariant) {
    const list = readAll();
    const next = typeof identityOrVariant === 'string' && identityOrVariant.includes('::')
      ? list.filter((item) => itemIdentityKey(item) !== identityOrVariant)
      : list.filter((item) => String(item.variantId) !== String(identityOrVariant));
    writeAll(next);
  }

  // -----------------------------------------------------------------------
  // Inventory helpers
  // -----------------------------------------------------------------------

  /**
   * Fetch /products/{handle}.js and return a map of variantId → inventory
   * info: { tracked: bool, qty: number, policy: string }.
   * Returns an empty object on any error so callers can skip the check.
   */
  function fetchProductInventory(handle) {
    if (!handle) return Promise.resolve({});
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((product) => {
        if (!product || !product.variants) return {};
        const map = {};
        product.variants.forEach((v) => {
          map[v.id] = {
            tracked: v.inventory_management === 'shopify',
            policy:  v.inventory_policy,
            // inventory_quantity is present in /products/handle.js when
            // inventory_management === 'shopify'. The typeof check avoids
            // treating a missing field as zero.
            qty: typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null,
          };
        });
        return map;
      })
      .catch(() => ({}));
  }

  /**
   * Fetch the current cart and return it as a plain object.
   * Returns null on error.
   */
  function fetchCurrentCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }

  /**
   * Sum how many units of a given variantId are already in the cart.
   */
  function getCartQtyForVariant(cart, variantId) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      return item.variant_id === variantId ? sum + item.quantity : sum;
    }, 0);
  }

  /**
   * Given a variant's inventory record and the current cart quantity,
   * return how many more units can be added (null = unlimited).
   */
  function computeAvailable(inv, cartQty) {
    if (!inv || !inv.tracked || inv.policy === 'continue' || inv.qty === null) {
      return null; // no inventory restriction
    }
    return Math.max(0, inv.qty - cartQty);
  }

  // -----------------------------------------------------------------------
  // Cart helpers
  // -----------------------------------------------------------------------

  // Sections we need re-rendered after any cart mutation. Keep in sync with
  // CartDrawer.getSectionsToRender() and CartItems.getSectionsToRender().
  const CART_SECTIONS_TO_RENDER = [
    { id: 'cart-drawer',       targetId: 'CartDrawer',           selector: '.drawer__inner' },
    { id: 'cart-icon-bubble',  targetId: 'cart-icon-bubble',     selector: '.shopify-section' },
    { id: 'main-cart-items',   targetId: 'main-cart-items',      selector: '.js-contents'   },
    { id: 'main-cart-footer',  targetId: 'main-cart-footer',     selector: '.js-contents'   },
  ];

  const CART_SECTIONS_PARAM = CART_SECTIONS_TO_RENDER.map((s) => s.id).join(',');

  function addItemsToCart(items) {
    return fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items:        items,
        sections:     CART_SECTIONS_PARAM,
        sections_url: window.location.pathname,
      }),
    }).then((r) => {
      if (!r.ok) return r.json().then((err) => Promise.reject(err));
      return r.json();
    });
  }

  /** Remove a cart line item by its unique key (sets quantity to 0). */
  function removeFromCart(itemKey) {
    return fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id:           itemKey,
        quantity:     0,
        sections:     CART_SECTIONS_PARAM,
        sections_url: window.location.pathname,
      }),
    }).then((r) => {
      if (!r.ok) return r.json().then((err) => Promise.reject(err));
      return r.json();
    });
  }

  /**
   * Apply a parsedState returned from /cart/change.js or /cart/add.js
   * (with sections requested) to the current page atomically.
   *
   * Handles:
   *   - Re-rendering each section's target innerHTML.
   *   - Toggling the `is-empty` class on cart-drawer / cart-items / footer.
   *   - Publishing PUB_SUB_EVENTS.cartUpdate with cartData so the rewards
   *     bar and other listeners refresh from a consistent snapshot.
   *   - Dispatching `cart:refresh` as a fallback for any older listeners.
   */
  function applyCartUpdate(parsedState) {
    if (!parsedState) return;
    return window.BSCartUI.refresh().catch(window.BSCartUI.reportError);
  }

  /**
   * Re-render the cart drawer (or notification popup, or icon bubble) after
   * a cart mutation. Also publishes PUB_SUB_EVENTS.cartUpdate so the cart
   * page's <cart-items> component self-refreshes when the customer is on /cart.
   *
   * Mirrors the exact same approach used in favorites.js so behaviour is
   * consistent across both modules.
   */
  function refreshCartUI() {
    return window.BSCartUI.refresh().catch(window.BSCartUI.reportError);
  }

  // -----------------------------------------------------------------------
  // Toast notification
  // -----------------------------------------------------------------------

  let toastEl    = null;
  let toastTimer = null;

  function showToast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'bs-sfl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
  }

  // -----------------------------------------------------------------------
  // Formatting helpers
  // -----------------------------------------------------------------------

  function formatMoney(cents) {
    const fmt = window.Shopify && Shopify.formatMoney || null;
    if (fmt && window.theme && window.theme.moneyFormat) {
      try { return fmt(cents, window.theme.moneyFormat); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  // -----------------------------------------------------------------------
  // Save for Later page — item rendering
  // -----------------------------------------------------------------------

  function buildSavedItemElement(item) {
    const li = document.createElement('li');
    li.className = 'bs-sfl-item';
    li.setAttribute('data-variant-id', item.variantId);
    li.setAttribute('data-saved-key', itemIdentityKey(item));

    const imgHtml = item.image
      ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">'
      : '<div class="bs-sfl-item__no-image"></div>';

    const isDefaultVariant = !item.variantTitle || item.variantTitle === 'Default Title';

    let priceHtml = '';
    if (item.compareAt && item.compareAt > item.price) {
      priceHtml =
        '<s class="bs-sfl-item__compare">' + formatMoney(item.compareAt) + '</s> ' +
        '<span class="bs-sfl-item__sale">'  + formatMoney(item.price)   + '</span>';
    } else if (item.price) {
      priceHtml = formatMoney(item.price);
    }

    li.innerHTML =
      '<div class="bs-sfl-item__media">' +
        '<a href="' + escapeHtml(item.url) + '">' + imgHtml + '</a>' +
      '</div>' +
      '<div class="bs-sfl-item__body">' +
        '<button type="button" class="bs-sfl-item__remove" data-sfl-remove>Remove</button>' +
        '<h2 class="bs-sfl-item__title">' +
          '<a href="' + escapeHtml(item.url) + '">' + escapeHtml(item.title) + '</a>' +
        '</h2>' +
        (isDefaultVariant ? '' : '<p class="bs-sfl-item__variant">' + escapeHtml(item.variantTitle) + '</p>') +
        (priceHtml ? '<p class="bs-sfl-item__price">' + priceHtml + '</p>' : '') +
        '<p class="bs-sfl-item__qty">Quantity: ' + item.quantity + '</p>' +
        '<div class="bs-sfl-item__actions">' +
          // Move to Cart button — may be replaced by markOutOfStock() after
          // async inventory check completes.
          '<button type="button" class="button button--primary bs-sfl-item__move" data-sfl-move>' +
            'Move to Cart' +
          '</button>' +
        '</div>' +
        '<p class="bs-sfl-item__status" hidden></p>' +
      '</div>';

    return li;
  }

  function setStatus(itemEl, message, kind) {
    const status = itemEl.querySelector('.bs-sfl-item__status');
    if (!status) return;
    status.textContent = message || '';
    status.hidden      = !message;
    status.classList.toggle('is-error',   kind === 'error');
    status.classList.toggle('is-success', kind === 'success');
  }

  /**
   * Update the item row to show "Out of stock" instead of the move button.
   * The item stays in the list — only the customer can remove it manually.
   */
  function markOutOfStock(itemEl) {
    const moveBtn = itemEl.querySelector('[data-sfl-move]');
    if (!moveBtn) return;
    moveBtn.disabled    = true;
    moveBtn.textContent = 'Out of stock';
    moveBtn.classList.add('is-out-of-stock');
    itemEl.setAttribute('data-out-of-stock', 'true');
  }

  // -----------------------------------------------------------------------
  // Move to Cart — with inventory check
  // -----------------------------------------------------------------------

  /**
   * Move a saved item back to the cart.
   *
   * Inventory check logic:
   *   1. Fetch /products/{handle}.js to get current variant inventory.
   *   2. Fetch /cart.js to see what's already in the cart for this variant.
   *   3. available = inventoryQty - cartQtyForVariant
   *   4. If available === 0 → mark out-of-stock, do not add.
   *   5. If saved.quantity > available → clamp to available, show partial toast.
   *   6. Add clamped quantity to cart.
   *   7. Remove from Saved for Later regardless (even partial move).
   *   8. Refresh cart UI.
   */
  /**
   * Open the cart drawer if one exists on the page. Safe to call from any
   * context — does nothing if the drawer element isn't present.
   */
  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      try { drawer.open(); } catch (e) { /* ignore */ }
    }
  }

  function buildCartAddItem(savedItem, quantity) {
    const lineItem = {
      id: parseInt(savedItem.variantId, 10),
      quantity: Math.max(1, parseInt(quantity, 10) || parseInt(savedItem.quantity, 10) || 1),
    };
    const properties = normalizeProperties(savedItem.properties);
    if (Object.keys(properties).length) {
      lineItem.properties = properties;
    }
    const sellingPlanId = parseSellingPlanId(savedItem.sellingPlanId || savedItem.selling_plan);
    if (sellingPlanId) {
      lineItem.selling_plan = sellingPlanId;
    }
    return lineItem;
  }

  /**
   * Pull the first integer out of a Shopify cart error description.
   * Examples handled:
   *   "You can only add 20 of this item to your cart."  → 20
   *   "All 20 are in your cart."                        → 20
   *   "There are only 20 left of Foo."                  → 20
   */
  function parseQtyFromError(err) {
    if (!err) return null;
    const text = (err.description || err.message || '').toString();
    const m    = text.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  }

  /**
   * Finalise a successful (full or partial) move to cart:
   * remove from SFL, refresh cart UI, open drawer, toast.
   */
  function finishMove(savedKey, itemEl, container, addedQty, requestedQty, addResponse) {
    remove(savedKey);
    if (itemEl && itemEl.parentNode) itemEl.remove();

    if (addedQty < requestedQty) {
      showToast('Only ' + addedQty + ' ' + (addedQty === 1 ? 'was' : 'were') +
        ' moved to cart due to availability.');
    } else {
      showToast('Item moved to cart.');
    }

    // Prefer the sections from the add.js response (atomic, no race). Fall
    // back to the full re-fetch path if the response didn't include them.
    if (addResponse && addResponse.sections) {
      applyCartUpdate(addResponse);
      openCartDrawer();
    } else {
      Promise.resolve(refreshCartUI()).then(openCartDrawer);
    }

    if (readAll().length === 0 && container) renderSavedForLaterTab(container);
  }

  function executeMoveToCart(savedKey, itemEl, moveBtn, container) {
    const saved = readAll().find((s) => itemIdentityKey(s) === savedKey);
    if (!saved) return;
    const variantId = parseInt(saved.variantId, 10);
    if (!Number.isSafeInteger(variantId) || variantId <= 0) return;

    moveBtn.disabled = true;
    setStatus(itemEl, 'Moving to cart…', null);

    // Strategy: capture cart qty for this variant BEFORE the add, attempt
    // the add (Shopify is the source of truth for inventory), then re-fetch
    // the cart and diff to find out how many units were actually added.
    // This works whether Shopify returns 200 (full add) or 422 (rejected).
    fetchCurrentCart()
      .then(function (preCart) {
        const preQty = getCartQtyForVariant(preCart, variantId);

        return addItemsToCart([buildCartAddItem(saved, saved.quantity)])
          .then(
            function (resp) { return { ok: true,  err: null, resp: resp }; },
            function (err)  { return { ok: false, err: err,  resp: null }; }
          )
          .then(function (addResult) {
            // If the first attempt failed, parse the allowed qty from the
            // error message and retry once with the clamped amount.
            if (!addResult.ok) {
              const maxAddable = parseQtyFromError(addResult.err);
              if (maxAddable && maxAddable > 0) {
                return addItemsToCart([buildCartAddItem(saved, maxAddable)])
                  .then(
                    function (resp) { return { ok: true,  err: null, resp: resp }; },
                    function (err)  { return { ok: false, err: err,  resp: null }; }
                  );
              }
            }
            return addResult;
          })
          .then(function (finalResult) {
            // Verify what actually landed in the cart by re-fetching it.
            return fetchCurrentCart().then(function (postCart) {
              const postQty = getCartQtyForVariant(postCart, variantId);
              const added   = Math.max(0, postQty - preQty);
              return { added: added, finalResult: finalResult };
            });
          });
      })
      .then(function (result) {
        const added   = result.added;
        const final   = result.finalResult || {};
        const err     = final.err;
        const addResp = final.resp;

        if (added > 0) {
          // Something was added — success path (full or partial).
          setStatus(itemEl, '', null);
          finishMove(savedKey, itemEl, container, added, saved.quantity, addResp);
          return;
        }

        // Nothing was added. Either out of stock or another error.
        const text = ((err && (err.description || err.message)) || '').toString();
        if (/sold\s*out|out of stock|unavailable|cannot find/i.test(text) ||
            parseQtyFromError(err) === 0) {
          markOutOfStock(itemEl);
          setStatus(itemEl, '', null);
        } else {
          setStatus(itemEl, text || 'Could not move to cart.', 'error');
          moveBtn.disabled = false;
        }
      })
      .catch(function (err) {
        // Network failure during pre-fetch.
        setStatus(itemEl, (err && err.description) || 'Could not move to cart.', 'error');
        moveBtn.disabled = false;
      });
  }

  // -----------------------------------------------------------------------
  // Async stock check — updates Move to Cart buttons after render
  // -----------------------------------------------------------------------

  /**
   * After the Saved for Later tab renders, run a background inventory check
   * for all saved items and mark any that are fully out of stock.
   * Deduplicates by product handle to minimise HTTP requests.
   */
  function checkSavedItemsStock(container) {
    const items = readAll();
    if (!items.length) return;

    // Collect unique handles that have a productHandle stored.
    const handles = [];
    items.forEach((item) => {
      if (item.productHandle && !handles.includes(item.productHandle)) {
        handles.push(item.productHandle);
      }
    });
    if (!handles.length) return;

    // Fetch inventory and cart in parallel.
    Promise.all([
      Promise.all(handles.map((h) => fetchProductInventory(h).then((map) => ({ handle: h, map })))),
      fetchCurrentCart(),
    ])
      .then(function (results) {
        const invResults = results[0]; // [{handle, map}, ...]
        const cart       = results[1];

        // Build a flat variantId → inv record from all fetched products.
        const invMap = {};
        invResults.forEach(({ map }) => {
          Object.keys(map).forEach((id) => { invMap[id] = map[id]; });
        });

        // Update each rendered item row.
        items.forEach((item) => {
          const savedKey = itemIdentityKey(item);
          const itemEl = container && container.querySelector(
            '.bs-sfl-item[data-saved-key="' + escapeAttrSelectorValue(savedKey) + '"]'
          );
          if (!itemEl) return;

          // Skip rows already marked (e.g. by a previous interaction).
          if (itemEl.getAttribute('data-out-of-stock') === 'true') return;

          const inv       = invMap[item.variantId];
          const cartQty   = getCartQtyForVariant(cart, item.variantId);
          const available = computeAvailable(inv, cartQty);

          if (available === 0) markOutOfStock(itemEl);
        });
      })
      .catch(() => { /* non-fatal — buttons stay enabled */ });
  }

  // -----------------------------------------------------------------------
  // Item event handlers
  // -----------------------------------------------------------------------

  function attachItemHandlers(itemEl, container) {
    itemEl.addEventListener('click', function (e) {
      // --- Remove from Saved for Later ---
      if (e.target.closest('[data-sfl-remove]')) {
        const savedKey = itemEl.getAttribute('data-saved-key');
        remove(savedKey || parseInt(itemEl.getAttribute('data-variant-id'), 10));
        itemEl.remove();
        if (readAll().length === 0) renderSavedForLaterTab(container);
        return;
      }

      // --- Move to Cart ---
      const moveBtn = e.target.closest('[data-sfl-move]');
      if (moveBtn && !moveBtn.disabled) {
        const savedKey = itemEl.getAttribute('data-saved-key');
        if (!savedKey) return;
        executeMoveToCart(savedKey, itemEl, moveBtn, container);
      }
    });
  }

  // -----------------------------------------------------------------------
  // Render the Saved for Later tab
  // -----------------------------------------------------------------------

  function renderSavedForLaterTab(container) {
    if (!container) container = document.querySelector('[data-bs-sfl-root]');
    if (!container) return;

    const items = readAll();
    if (items.length === 0) {
      container.innerHTML = '<div class="bs-favorites__empty"><p>No items saved for later.</p></div>';
      return;
    }

    container.innerHTML = '<ul class="bs-sfl-list"></ul>';
    const list = container.querySelector('.bs-sfl-list');
    items.forEach((item) => {
      const el = buildSavedItemElement(item);
      attachItemHandlers(el, container);
      list.appendChild(el);
    });

    // Run async inventory check to mark out-of-stock items. This runs after
    // the list is already visible so the tab feels instant.
    checkSavedItemsStock(container);
  }

  // -----------------------------------------------------------------------
  // Sign-in modal (shown when a guest tries to save an item)
  //
  // Reuses the same .bs-favorite-modal CSS classes so no extra styles are
  // needed. Only the heading/body text and data attributes differ.
  // -----------------------------------------------------------------------

  let sflModalEl = null;

  function openSflSignInModal() {
    if (!sflModalEl) {
      sflModalEl = document.createElement('div');
      sflModalEl.className = 'bs-favorite-modal';
      sflModalEl.setAttribute('role', 'dialog');
      sflModalEl.setAttribute('aria-modal', 'true');
      sflModalEl.setAttribute('aria-hidden', 'true');
      sflModalEl.innerHTML =
        '<div class="bs-favorite-modal__overlay" data-bs-sfl-close></div>' +
        '<div class="bs-favorite-modal__panel">' +
          '<button type="button" class="bs-favorite-modal__close" aria-label="Close" data-bs-sfl-close>&times;</button>' +
          '<h2 class="bs-favorite-modal__title">Sign in to save for later</h2>' +
          '<p class="bs-favorite-modal__text">Please sign in to your account to save items for later.</p>' +
          '<div class="bs-favorite-modal__actions">' +
            '<a class="button button--primary" data-bs-sfl-login>Sign in</a>' +
            '<button type="button" class="button button--secondary" data-bs-sfl-close>Cancel</button>' +
          '</div>' +
        '</div>';
      document.body.appendChild(sflModalEl);

      sflModalEl.addEventListener('click', function (e) {
        if (e.target.closest('[data-bs-sfl-close]')) closeSflSignInModal();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeSflSignInModal();
      });
    }

    const loginUrl = (document.querySelector('meta[name="bs-login-url"]') || {}).content || '/account/login';
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    const link     = sflModalEl.querySelector('[data-bs-sfl-login]');
    link.href      = loginUrl + (loginUrl.indexOf('?') === -1 ? '?' : '&') + 'checkout_url=' + returnTo;

    sflModalEl.classList.add('is-open');
    sflModalEl.setAttribute('aria-hidden', 'false');
  }

  function closeSflSignInModal() {
    if (!sflModalEl) return;
    sflModalEl.classList.remove('is-open');
    sflModalEl.setAttribute('aria-hidden', 'true');
  }

  // -----------------------------------------------------------------------
  // Save button handler (wired to cart drawer + main cart page)
  //
  // The Liquid template stamps all needed item data as data-* attributes on
  // the Save button so we never need an extra fetch at save time.
  // -----------------------------------------------------------------------

  function handleSaveForLaterClick(e) {
    const btn = e.target.closest('[data-bs-sfl-save]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    e.stopPropagation();

    // Guests cannot use Save for Later — prompt them to sign in.
    if (!getCustomerId()) {
      openSflSignInModal();
      return;
    }

    const itemKey      = btn.getAttribute('data-item-key');
    const variantId    = parseInt(btn.getAttribute('data-variant-id'), 10);
    const title        = btn.getAttribute('data-title')        || '';
    const variantTitle = btn.getAttribute('data-variant-title') || '';
    const image        = btn.getAttribute('data-image')        || null;
    const url          = btn.getAttribute('data-url')          || '/';
    const quantity     = parseInt(btn.getAttribute('data-quantity'),   10) || 1;
    const price        = parseInt(btn.getAttribute('data-price'),       10) || 0;
    const compareAt    = parseInt(btn.getAttribute('data-compare-at'), 10) || null;
    const handle       = btn.getAttribute('data-product-handle') || '';
    const sellingPlanId = parseSellingPlanId(btn.getAttribute('data-selling-plan-id'));
    let properties = {};
    try {
      const raw = btn.getAttribute('data-properties') || '{}';
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        properties = parsed;
      }
    } catch (e) {
      properties = {};
    }

    if (!itemKey || !variantId) return;

    // Disable immediately to prevent double-click.
    btn.disabled = true;

    removeFromCart(itemKey)
      .then(function (parsedState) {
        save({
          variantId,
          productHandle: handle,
          title,
          variantTitle,
          image,
          url,
          quantity,
          price,
          compareAt: compareAt && compareAt > price ? compareAt : null,
          properties,
          sellingPlanId,
        });
        showToast('Item saved for later.');

        // Atomically re-render cart UI from the change.js response. This
        // includes the proper empty-state markup when the saved item was
        // the last thing in the cart.
        try { applyCartUpdate(parsedState); } catch (e) { /* swallow */ }
      })
      .catch(function (err) {
        // Cart removal itself failed — re-enable so the customer can retry.
        btn.disabled = false;
        showToast((err && err.description) || 'Could not save item.');
      });
    // btn.disabled intentionally stays true on success: the entire row is
    // removed from the cart when applyCartUpdate() re-renders the section.
  }

  document.addEventListener('click', handleSaveForLaterClick);

  // -----------------------------------------------------------------------
  // Cart inventory validation on page load
  //
  // On every page load we compare cart quantities against live inventory.
  // If any item exceeds available stock we silently correct the cart and
  // notify the customer with a toast. This catches the case where inventory
  // ran out on another device or while the cart was sitting idle.
  // -----------------------------------------------------------------------

  function validateCartInventoryOnLoad() {
    return fetchCurrentCart()
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) return;

        // Deduplicate product handles to minimise fetch count.
        const handles = [];
        cart.items.forEach(function (item) {
          if (item.handle && !handles.includes(item.handle)) handles.push(item.handle);
        });

        // Fetch all products in parallel.
        return Promise.all(
          handles.map((h) =>
            fetch('/products/' + encodeURIComponent(h) + '.js', {
              headers: { Accept: 'application/json' },
            })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        ).then(function (products) {
          // Build variantId → { tracked, qty, policy } map from all products.
          const invMap = {};
          products.forEach(function (p) {
            if (!p || !p.variants) return;
            p.variants.forEach(function (v) {
              invMap[v.id] = {
                tracked: v.inventory_management === 'shopify',
                policy:  v.inventory_policy,
                qty:     typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null,
              };
            });
          });

          // Compare each cart item against available inventory.
          // Corrections are collected as line-key updates so split lines
          // (same variant, different properties/selling plan) stay isolated.
          const lineUpdates = [];
          let hadRemoved   = false;
          let hadReduced   = false;

          const itemsByVariant = {};
          cart.items.forEach(function (item) {
            const key = String(item.variant_id);
            if (!itemsByVariant[key]) itemsByVariant[key] = [];
            itemsByVariant[key].push(item);
          });

          Object.keys(itemsByVariant).forEach(function (variantId) {
            const inv = invMap[variantId];
            // Only act on tracked variants that enforce inventory limits.
            if (!inv || !inv.tracked || inv.policy === 'continue' || inv.qty === null) return;

            let remaining = Math.max(0, inv.qty);
            itemsByVariant[variantId].forEach(function (item) {
              const nextQty = Math.max(0, Math.min(item.quantity, remaining));
              if (item.quantity > nextQty && item.key) {
                lineUpdates.push({ id: item.key, quantity: nextQty });
                if (nextQty === 0) hadRemoved = true;
                else hadReduced = true;
              }
              remaining = Math.max(0, remaining - nextQty);
            });
          });

          if (lineUpdates.length === 0) return; // nothing to do

          const finishRenderBatch = window.BSCartUI?.beginBatch?.();
          const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';

          let chain = Promise.resolve();
          lineUpdates.forEach(function (update) {
            chain = chain.then(function () {
              return fetch(changeUrl, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body:    JSON.stringify({ id: update.id, quantity: update.quantity }),
              }).then(function (r) { return r.ok ? r.json() : null; });
            });
          });

          return chain
            .finally(function () { finishRenderBatch?.(); })
            .then(function () {
              // Notify the customer. Out-of-stock removal takes priority.
              if (hadRemoved) {
                showToast('An out-of-stock item was removed from your cart.');
              } else if (hadReduced) {
                showToast('Some cart quantities were updated due to availability.');
              }
              // Refresh whatever cart UI is currently visible.
              return refreshCartUI();
            })
            .catch(() => { /* silent fail — cart state unchanged */ });
        });
      })
      .catch(() => { /* silent fail on network error */ });
  }

  // -----------------------------------------------------------------------
  // Tab switching on the Favorites / Saved for Later page
  // -----------------------------------------------------------------------

  function initTabs() {
    const tabsEl = document.querySelector('[data-bs-page-tabs]');
    if (!tabsEl) return;

    const tabBtns = tabsEl.querySelectorAll('[data-bs-tab]');
    const panels  = document.querySelectorAll('[data-bs-tab-panel]');

    function activateTab(tabName) {
      tabBtns.forEach(function (btn) {
        const active = btn.getAttribute('data-bs-tab') === tabName;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panels.forEach(function (panel) {
        const active = panel.getAttribute('data-bs-tab-panel') === tabName;
        panel.hidden = !active;
        if (active && tabName === 'saved') {
          renderSavedForLaterTab(panel.querySelector('[data-bs-sfl-root]'));
        }
      });
    }

    tabsEl.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-bs-tab]');
      if (!btn) return;
      const name = btn.getAttribute('data-bs-tab');
      activateTab(name);
      history.replaceState(null, '', '#' + name);
    });

    // Honour URL hash on load.
    const hash      = (location.hash || '').replace('#', '');
    const validTabs = Array.from(tabBtns).map((b) => b.getAttribute('data-bs-tab'));
    activateTab(validTabs.includes(hash) ? hash : (validTabs[0] || 'favorites'));
  }

  // -----------------------------------------------------------------------
  // Init
  // -----------------------------------------------------------------------

  function init() {
    hydrate();
    initTabs();
    // Run cart validation after a short idle so it doesn't compete with
    // above-the-fold rendering.
    setTimeout(validateCartInventoryOnLoad, 800);
  }

  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') init();

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------
  window.BSSavedForLater = {
    save:                    save,
    remove:                  remove,
    readAll:                 readAll,
    itemIdentityKey:         itemIdentityKey,
    buildCartAddItem:        buildCartAddItem,
    showToast:               showToast,
    renderSavedForLaterTab:  renderSavedForLaterTab,
    validateCartInventory:   validateCartInventoryOnLoad,
  };
})();
